import { z } from 'zod';

import { Email, Id, IsoDateTime, Paise, Password, PhoneIN } from '../common/primitives';

// ── Customer ─────────────────────────────────────────────────────────────────

export const CustomerSchema = z.object({
  id: Id,
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  status: z.string(),
  emailVerified: z.boolean(),
  phoneVerified: z.boolean(),
  isSuspicious: z.boolean(),
  blocked: z.boolean(),
  verified: z.boolean(),
  packageName: z.string().nullable(),
  walletBalancePaise: Paise,
  createdAt: IsoDateTime,
});
export type Customer = z.infer<typeof CustomerSchema>;

export const CreateCustomerRequest = z
  .object({
    name: z.string().trim().min(2).max(120),
    email: Email,
    phone: PhoneIN,
    password: Password,
    classifiedPackageId: Id.optional(),
  })
  .strict();
export type CreateCustomerRequest = z.infer<typeof CreateCustomerRequest>;

export const UpdateCustomerRequest = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    phone: PhoneIN.optional(),
    isSuspicious: z.boolean().optional(),
    blocked: z.boolean().optional(),
    classifiedPackageId: Id.nullable().optional(),
  })
  .strict();
export type UpdateCustomerRequest = z.infer<typeof UpdateCustomerRequest>;

export const ListCustomersQuery = z
  .object({
    q: z.string().trim().max(200).optional(),
    verification: z.enum(['VERIFIED', 'UNVERIFIED']).optional(),
  })
  .strict();
export type ListCustomersQuery = z.infer<typeof ListCustomersQuery>;

// ── Classified package ───────────────────────────────────────────────────────

export const ClassifiedPackageSchema = z.object({
  id: Id,
  name: z.string(),
  description: z.string().nullable(),
  pricePaise: Paise,
  durationDays: z.number().int(),
  adLimit: z.number().int(),
  active: z.boolean(),
  createdAt: IsoDateTime,
});
export type ClassifiedPackage = z.infer<typeof ClassifiedPackageSchema>;

export const UpsertClassifiedPackageRequest = z
  .object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(2000).optional(),
    pricePaise: Paise,
    durationDays: z.number().int().min(1).max(3650).optional(),
    adLimit: z.number().int().min(1).max(100000).optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type UpsertClassifiedPackageRequest = z.infer<typeof UpsertClassifiedPackageRequest>;

// ── Classified product ───────────────────────────────────────────────────────

export const ClassifiedStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export type ClassifiedStatus = z.infer<typeof ClassifiedStatusSchema>;

export const ClassifiedProductSchema = z.object({
  id: Id,
  name: z.string(),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  pricePaise: Paise,
  uploadedById: Id,
  uploadedByName: z.string().nullable(),
  status: ClassifiedStatusSchema,
  published: z.boolean(),
  createdAt: IsoDateTime,
});
export type ClassifiedProduct = z.infer<typeof ClassifiedProductSchema>;

export const UpdateClassifiedProductRequest = z
  .object({
    status: ClassifiedStatusSchema.optional(),
    published: z.boolean().optional(),
  })
  .strict();
export type UpdateClassifiedProductRequest = z.infer<typeof UpdateClassifiedProductRequest>;

// ── Customer 360 ─────────────────────────────────────────────────────────────

export const Customer360ResponseSchema = z.object({
  customer: CustomerSchema.extend({
    marketingConsent: z.boolean().optional(),
    whatsappConsent: z.boolean().optional(),
  }),
  orders: z.array(
    z.object({
      id: z.string(),
      orderGroupId: z.string(),
      status: z.string(),
      totalPaise: z.number(),
      createdAt: IsoDateTime,
    }),
  ),
  wallet: z.array(
    z.object({
      id: z.string(),
      remainingPaise: z.number(),
      status: z.string(),
      bucket: z.string(),
      expiresAt: IsoDateTime.nullable(),
      createdAt: IsoDateTime,
    }),
  ),
  support: z.array(
    z.object({
      id: z.string(),
      ticketNumber: z.string(),
      category: z.string(),
      subject: z.string().nullable(),
      status: z.string(),
      priority: z.string(),
      createdAt: IsoDateTime,
    }),
  ),
  risk: z.object({
    churnRiskBps: z.number(),
    signals: z.array(
      z.object({
        id: z.string(),
        kind: z.string(),
        severity: z.string(),
        score: z.number(),
        status: z.string(),
        createdAt: IsoDateTime,
      }),
    ),
  }),
  addresses: z.array(
    z.object({
      id: z.string(),
      label: z.string().nullable(),
      contactName: z.string(),
      contactPhone: z.string(),
      city: z.string(),
      state: z.string(),
      pincode: z.string(),
      isDefault: z.boolean(),
    }),
  ),
  notes: z.array(
    z.object({
      id: z.string(),
      type: z.string(),
      description: z.string(),
      createdAt: IsoDateTime,
    }),
  ),
});
export type Customer360Response = z.infer<typeof Customer360ResponseSchema>;

